// @ts-nocheck
import { measureApi, timeZoneApi } from '@/api/config-setup'
import Select from '@/components/form-control/Select'
import { ADMIN_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import useUpdateUserSettings from '@/features/auth/hooks/useUpdateUserSettings'
import useUser from '@/features/auth/hooks/useUser'
import { NotifyService, setupHelper } from '@/helpers'
import { changeTheme } from '@/store/slices/themeSlice'
import { DATA_PAGINATION, OptionItem } from '@/types'
import { User } from '@/types/user'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Stack, Typography } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

type Props = {}

interface FormInputs {
  measure: OptionItem
  timeZone: OptionItem
}

const defaultValues: Partial<FormInputs> = {
  measure: undefined,
  timeZone: undefined,
}

const LayoutCenter = (props: Props) => {
  const navigate = useNavigate()
  const { data: user } = useUser<User>()
  const updateSettings = useUpdateUserSettings()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(changeTheme('dark'))
  }, [])

  const schema = useMemo(() => {
    return yup
      .object({
        measure: yup.mixed().required('Unit of Measurement is required'),
        timeZone: yup.mixed().required('Time Zone is required'),
      })
      .required()
  }, [])

  const form = useForm<FormInputs>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const { handleSubmit, reset } = form

  useEffect(() => {
    if (!user) return
    if (
      setupHelper.isDoneAccountConfig(user) &&
      (user.role === 'SuperAdmin' || user.role === 'Admin')
    ) {
      navigate(ADMIN_ROUTES.index.to)
      return
    }

    if (
      setupHelper.isDoneAccountConfig(user) &&
      (user.role === 'Manager' || user.role === 'User')
    ) {
      navigate(OPERATIONS_ROUTES.index.to)
      return
    }
    reset({
      measure: user.measure
        ? { id: user.measure.id, name: user.measure.name }
        : undefined,
      timeZone: user.gmtTimeZone
        ? {
            id: user.gmtTimeZone.id,
            zoneId: user.gmtTimeZone.zoneId,
            displayName: user.gmtTimeZone.displayName,
          }
        : undefined,
    })
  }, [user, reset, navigate])

  const onSubmit = async (data: FormInputs) => {
    // Write to the IS (PUT /api/users/me/settings) — the source GET /api/users/me
    // reads back. measure is the string measure id; zoneInfo is the IANA tz string.
    updateSettings({
      measure: String(data.measure.id),
      zoneInfo: data.timeZone.zoneId,
    })
  }

  const getOptions = (key: keyof FormInputs) => async () => {
    let data: DATA_PAGINATION | undefined
    switch (key) {
      case 'measure':
        data = await measureApi
          .getAll()
          .then(({ data }) => ({
            data: data,
            page: 1,
            size: data.length,
            totalCount: data.length,
            totalPage: 1,
          }))
          .catch((e) => {
            NotifyService.error(e)
            return undefined
          })
        break
      case 'timeZone':
        data = await timeZoneApi
          .getAll()
          .then(({ data }) => ({
            data: data,
            page: 1,
            size: data.length,
            totalCount: data.length,
            totalPage: 1,
          }))
          .catch((e) => {
            NotifyService.error(e)
            return undefined
          })
        break
    }
    return data
  }

  return (
    <Stack
      sx={{ mt: '52px' }}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack sx={{ alignItems: 'center' }}>
        <Typography sx={{ fontSize: '20px', lineHeight: '2.4' }}>
          Account Configurations
        </Typography>
      </Stack>

      <Stack spacing={2}>
        <Select
          label="Unit of Measurement"
          name="measure"
          bindLabel="name"
          form={form}
          getOptions={getOptions('measure')}
        />
        <Select
          label="Time Zone"
          name="timeZone"
          bindLabel="displayName"
          form={form}
          getOptions={getOptions('timeZone')}
        />
      </Stack>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: '16px' }}>
        <Stack justifyContent="center">
          <Typography sx={{ fontSize: '13px', color: '#5C5C5C' }}>
            These settings can be changed later
          </Typography>
        </Stack>
        <Button
          type="submit"
          sx={{
            borderRadius: '4px',
            maxHeight: '26px',
            width: '72px',
            textDecoration: 'none',
          }}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  )
}

export default LayoutCenter
